class ComponentScroll extends Theme.Component {
  #state = "idle";
  #drag_start = 0;
  #drag_threshold = 10;
  #drag_delta = 0;
  #scroll_start = 0;
  #scroll_container = null;
  #animation_frame_id = null;
  #snap_points = [];

  connectedCallback() {
    this.defineElements({
      container: '[data-element="scroll-container"]'
    });

    this.defineElementCollections({
      items: ':scope > [data-element="scroll-container"] > div > *'
    });

    this.setStyles();
    this.setSnapPoints();

    this.addDirectEvent("resize", window, Theme.Helpers.debounce(this.onResize, 150));
    this.addDirectEvent("pointerdown", this.elements.container, this.onPointerDown);
    this.addDirectEvent("pointermove", this.elements.container, this.onPointerMove);
    this.addDirectEvent("pointerup", this.elements.container, this.onPointerUp);

    this.addDirectEvent("dragstart", this.elements.container, (event) => {
      event.preventDefault()
    });

    this.addActionEvents();
  }

  setStyles() {
    this.updateScrollbarCompensation();

    if (this.isScrollable()) {
      this.classList.add("state--scrollable");
    } else {
      this.classList.remove("state--scrollable");
    }
  }

  updateScrollbarCompensation() {
    const scrollbar_width = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbar_width > 0) {
      this.elements.container.style.setProperty("--scrollbar-compensation", `${scrollbar_width / 2}px`);
    } else {
      this.elements.container.style.removeProperty("--scrollbar-compensation");
    }
  }

  setSnapPoints() {
    const container = this.elements.container;
    const container_box = container.getBoundingClientRect();
    const container_style = getComputedStyle(container);
    const container_padding_left = parseFloat(container_style.paddingLeft);
    const container_edge = container_box.left + container_padding_left;
    const container_scroll = container.scrollLeft;

    const max = container.scrollWidth - container.clientWidth;

    const points = [];

    this.elements.items.forEach((element) => {
      const element_box = element.getBoundingClientRect();
      const item_scroll = element_box.left - container_edge;
      const distance = container_scroll + item_scroll;
      const clamped = Math.max(0, Math.min(distance, max));
      const rounded = Math.round(clamped);

      if (!points.includes(rounded)) {
        points.push(rounded);
      }
    });

    this.#snap_points = points;
  }

  isScrollable() {
    const container = this.elements.container;

    return (container.scrollWidth - container.clientWidth) > 1;
  }

  onResize() {
    this.setStyles();
    this.setSnapPoints();
    this.scrollToNearestSnapPoint();
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (!this.isScrollable()) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.#state = "tracking";
    this.#scroll_container = this.getDragContainer(event);
    this.#scroll_start = this.#scroll_container.scrollLeft;
    this.#drag_start = event.pageX;
  }

  onPointerMove(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    this.#drag_delta = event.pageX - this.#drag_start;

    if (this.isDrag() && this.#state === "tracking") {
      this.#scroll_container.setPointerCapture(event.pointerId);
      this.#state = "dragging";
      this.classList.add("state--dragging");
      this.startAnimationLoop();
    }
  }

  onPointerUp(event) {
    if (event.pointerType === "touch") {
      this.reset();
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    const distance = Math.abs(this.#scroll_container.scrollLeft - this.#scroll_start);

    if (this.#state === "dragging" && event.pointerType === "mouse") {
      this.preventDefaultClick();
    }

    if (this.#scroll_container === this.elements.container && (this.#state === "dragging" || distance >= this.#drag_threshold)) {
      this.animateScrollTo(this.getDirectionalSnapPoint());
    }

    this.reset();
  }

  isDrag() {
    const threshold = 10;
    const distance = Math.abs(this.#drag_delta);

    return distance >= threshold;
  }

  getDragContainer(event) {
    const scroll = event.target.closest(".scroll");

    if (scroll && this.elements.container.contains(scroll)) {
      return scroll;
    }

    return this.elements.container;
  }

  startAnimationLoop() {
    if (this.#animation_frame_id) {
      return;
    }

    const loop = () => {
      this.#scroll_container.scrollLeft = this.#scroll_start - this.#drag_delta;
      this.#animation_frame_id = requestAnimationFrame(loop);
    };

    this.#animation_frame_id = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    cancelAnimationFrame(this.#animation_frame_id);
    this.#animation_frame_id = null;
  }

  preventDefaultClick() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    }, {
      capture: true,
      once: true
    });
  }

  animateScrollTo(value) {
    this.elements.container.scrollTo({
      left: value,
      behavior: "smooth"
    });
  }

  scrollToNearestSnapPoint() {
    if (!this.#snap_points.length) {
      return;
    }

    this.elements.container.scrollTo({
      left: this.getNearestSnapPoint()
    });
  }

  getNearestSnapPoint() {
    const scroll_left = this.elements.container.scrollLeft;

    return this.#snap_points.reduce((nearest, point) => {
      const nearest_distance = Math.abs(nearest - scroll_left);
      const point_distance = Math.abs(point - scroll_left);

      return point_distance < nearest_distance ? point : nearest;
    });
  }

  getDirectionalSnapPoint() {
    const container_scroll = this.elements.container.scrollLeft;

    if (!this.#snap_points.length) {
      return container_scroll;
    }

    const direction = Math.sign(container_scroll - this.#scroll_start);

    if (direction > 0) {
      return this.getNextSnapPoint();
    }

    if (direction < 0) {
      return this.getPreviousSnapPoint();
    }

    return container_scroll;
  }

  getNextSnapPoint() {
    const points = this.#snap_points;
    const next = points.find((p) => p > this.elements.container.scrollLeft);

    return next ?? points.at(-1);
  }

  getPreviousSnapPoint() {
    const points = this.#snap_points;
    const previous = points.toReversed().find((p) => p < this.elements.container.scrollLeft);

    return previous ?? points[0];
  }

  nextItem() {
    this.animateScrollTo(this.getNextSnapPoint());
  }

  previousItem() {
    this.animateScrollTo(this.getPreviousSnapPoint());
  }

  nextGroup() {
    const group_size = this.getGroupSize();
    const tolerance = 0.5;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (this.elements.container.scrollLeft - tolerance);
    });

    const index = Math.min(current_index + group_size, this.#snap_points.length - 1);

    this.animateScrollTo(this.#snap_points[index]);
  }

  previousGroup() {
    const group_size = this.getGroupSize();
    const tolerance = 0.5;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (this.elements.container.scrollLeft - tolerance);
    });

    const index = Math.max(current_index - group_size, 0);

    this.animateScrollTo(this.#snap_points[index]);
  }

  getGroupSize() {
    const container_width = this.elements.container.clientWidth;
    const item_width = this.elements.items[0].clientWidth;

    return Math.floor(container_width / item_width);
  }

  reset() {
    this.stopAnimationLoop();
    this.#state = "idle";
    this.#drag_start = 0;
    this.#scroll_start = 0;
    this.#scroll_container = null;
    this.classList.remove("state--dragging");
  }
}

customElements.define("component-scroll", ComponentScroll);
